import React, { useEffect, useRef, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import VisibilityIcon from "@material-ui/icons/Visibility";
import QRCode from "qrcode.react";
import { toast } from "react-toastify";

import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(3),
    minWidth: 320,
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  codeBox: {
    background: theme.palette.type === "dark" ? "#222" : "#f5f5f5",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    wordBreak: "break-all",
    fontSize: 13,
    marginBottom: theme.spacing(1),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2),
    background: "#ffffff",
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  paidBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
    gap: theme.spacing(1),
    textAlign: "center",
  },
}));

const SubscriptionModal = ({ open, onClose, Invoice }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(Invoice);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [boletoLoading, setBoletoLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    setInvoice(Invoice);
    setError(null);
    setPaid(Invoice?.status === "paid");
  }, [Invoice]);

  useEffect(() => {
    if (!open || !invoice?.id) return;

    // Já tem boleto/Pix gerado: mostra na hora, sem chamar a API de novo.
    if (invoice.interLinhaDigitavel || invoice.interPixCopiaCola) return;

    const gerarCobranca = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.post(
          `/invoices/${invoice.id}/gerar-cobranca-inter`
        );
        setInvoice(data);
      } catch (err) {
        setError(
          err?.response?.data?.error ||
            "Não foi possível gerar a cobrança agora. Tente novamente em instantes."
        );
      }
      setLoading(false);
    };

    gerarCobranca();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id, retryCount]);

  // Enquanto o modal estiver aberto e a cobrança já tiver sido gerada,
  // consulta o status a cada 5s pra detectar o pagamento quase na hora.
  useEffect(() => {
    const podeChecar =
      open &&
      invoice?.id &&
      !paid &&
      (invoice.interPixCopiaCola || invoice.interLinhaDigitavel);

    if (!podeChecar) {
      return undefined;
    }

    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(
          `/invoices/${invoice.id}/status-pagamento-inter`
        );
        if (data.status === "paid") {
          setPaid(true);
          setInvoice(data);
        }
      } catch (_) {
        // silencioso: só tenta de novo no próximo ciclo
      }
    }, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice?.id, paid, invoice?.interPixCopiaCola, invoice?.interLinhaDigitavel]);

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado!`);
  };

  const handleCloseAfterPaid = () => {
    onClose();
    window.location.reload();
  };

  const handleViewBoleto = async () => {
    setBoletoLoading(true);
    try {
      const { data } = await api.get(`/invoices/${invoice.id}/boleto-pdf`);
      const byteChars = atob(data.pdf);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Não foi possível abrir o boleto agora."
      );
    }
    setBoletoLoading(false);
  };

  const valorFormatado = invoice?.value
    ? Number(invoice.value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
    : "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Pagamento da fatura
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        {paid && (
          <div className={classes.paidBox}>
            <CheckCircleIcon style={{ fontSize: 56, color: "#2e7d32" }} />
            <Typography variant="h6">Pagamento confirmado!</Typography>
            <Typography variant="body2" color="textSecondary">
              Sua fatura foi paga e o vencimento já foi renovado.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseAfterPaid}
            >
              Fechar
            </Button>
          </div>
        )}

        {!paid && loading && (
          <div className={classes.loadingBox}>
            <CircularProgress />
            <Typography variant="body2">Gerando cobrança...</Typography>
          </div>
        )}

        {!paid && !loading && error && (
          <div className={classes.loadingBox}>
            <Typography color="error" variant="body2" align="center">
              {error}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setRetryCount((c) => c + 1)}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {!paid && !loading && !error && invoice && (
          <>
            <Typography variant="h6" gutterBottom>
              {valorFormatado}
            </Typography>

            {invoice.interPixCopiaCola && (
              <div className={classes.section}>
                <Typography variant="subtitle2" gutterBottom>
                  💠 Pague com Pix (escaneie ou copie)
                </Typography>
                <div className={classes.qrContainer}>
                  <QRCode
                    value={invoice.interPixCopiaCola}
                    size={200}
                    fgColor="#2d3748"
                    bgColor="#ffffff"
                    level="M"
                  />
                </div>
                <div className={classes.codeBox}>
                  {invoice.interPixCopiaCola}
                </div>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<FileCopyIcon />}
                  onClick={() =>
                    handleCopy(invoice.interPixCopiaCola, "Código Pix")
                  }
                >
                  Copiar código Pix
                </Button>
              </div>
            )}

            {invoice.interLinhaDigitavel && (
              <div className={classes.section}>
                <Typography variant="subtitle2" gutterBottom>
                  📋 Ou pague com o boleto
                </Typography>
                <div className={classes.codeBox}>
                  {invoice.interLinhaDigitavel}
                </div>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FileCopyIcon />}
                  onClick={() =>
                    handleCopy(invoice.interLinhaDigitavel, "Linha digitável")
                  }
                  style={{ marginBottom: 8 }}
                >
                  Copiar linha digitável
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={
                    boletoLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <VisibilityIcon />
                    )
                  }
                  disabled={boletoLoading}
                  onClick={handleViewBoleto}
                >
                  Ver boleto
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;
